import { PerformanceStageText } from '../constants/business/performance';

type TabMode = 'none' | 'reuse' | 'new'; // none: 不显示tab, reuse: 复用tab, new: 新开tab
type CacheStrategy = 'none' | 'state' | 'instance'; // none: 不缓存, state: 缓存到state, instance: 缓存到instance

type RouteMatchContext = {
  path: string;
  search: string;
  fullPath: string;
  params: Record<string, string>;
};

export type RouteTabMeta = {
  title: string;
  tabMode: TabMode;
  cacheStrategy: CacheStrategy;
  getTabKey: (ctx: RouteMatchContext) => string;
  getTitle?: (ctx: RouteMatchContext) => string;
  /** 未设置时与 tabKey 一致；人员角色子页等需「单标签 + 多实例缓存」时拆开 */
  getKeepAliveCacheKey?: (ctx: RouteMatchContext) => string;
};

type RouteMetaEntry = {
  pattern: string;
  meta: RouteTabMeta;
};

type QueryKeyPolicyEntry = {
  pattern: string;
  ignoreKeys: string[];
};

const DEFAULT_TAB_KEY_IGNORE_KEYS = ['tab', 'hcPage', 'page', 'currentPage'];

const tabKeyQueryPolicyTable: QueryKeyPolicyEntry[] = [
  // 默认沿用全局策略，后续如需路由级覆写可在这里扩展
];

const buildPathWithIgnoredQuery = (
  path: string,
  search: string,
  ignoreKeys: string[]
) => {
  const searchParams = new URLSearchParams(search.replace(/^\?/, ''));
  ignoreKeys.forEach((key) => {
    searchParams.delete(key);
  });
  const nextSearch = searchParams.toString();
  return nextSearch ? `${path}?${nextSearch}` : path;
};

const createRegExpFromPattern = (pattern: string) => {
  const keys: string[] = [];
  const escaped = pattern
    .split('/')
    .map((segment) => {
      if (!segment) return '';
      if (segment.startsWith(':')) {
        keys.push(segment.slice(1));
        return '([^/]+)';
      }
      return segment.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    })
    .join('/');
  return {
    keys,
    regexp: new RegExp(`^${escaped}$`, 'i'),
  };
};

const matchPattern = (pattern: string, path: string) => {
  const { keys, regexp } = createRegExpFromPattern(pattern);
  const matched = path.match(regexp);
  if (!matched) return null;
  const params: Record<string, string> = {};
  keys.forEach((key, index) => {
    params[key] = matched[index + 1] || '';
  });
  return params;
};

export const resolveTabKeyIgnoreKeys = (path: string) => {
  for (const entry of tabKeyQueryPolicyTable) {
    if (matchPattern(entry.pattern, path)) {
      return entry.ignoreKeys;
    }
  }
  return DEFAULT_TAB_KEY_IGNORE_KEYS;
};

export const buildTabRouteCacheKey = (path: string, search: string) => {
  return buildPathWithIgnoredQuery(path, search, resolveTabKeyIgnoreKeys(path));
};

const detailTabMeta = (title: string): RouteTabMeta => ({
  title,
  tabMode: 'new',
  cacheStrategy: 'instance',
  getTabKey: ({ path, params }) =>
    `${path.split('/:')[0]}:${params.id || path}`,
});

/** 动态段名不是 `id` 时（如 `:groupId`、`:code`、`:dataCode`） */
const detailTabMetaWithParam = (
  title: string,
  paramName: string
): RouteTabMeta => ({
  title,
  tabMode: 'new',
  cacheStrategy: 'instance',
  getTabKey: ({ path, params }) =>
    `${path.split('/:')[0]}:${params[paramName] || path}`,
});

const reuseTabMeta = (title: string): RouteTabMeta => ({
  title,
  tabMode: 'reuse',
  cacheStrategy: 'instance',
  getTabKey: ({ path }) => path,
});

const buildPathWithPickedQuery = (
  path: string,
  search: string,
  pickedKeys: string[]
) => {
  const searchParams = new URLSearchParams(search.replace(/^\?/, ''));
  const nextSearchParams = new URLSearchParams();

  pickedKeys.forEach((key) => {
    const value = searchParams.get(key);
    if (value) {
      nextSearchParams.set(key, value);
    }
  });

  const nextSearch = nextSearchParams.toString();
  return nextSearch ? `${path}?${nextSearch}` : path;
};

const queryTabMeta = (
  title: string,
  pickedKeys: string[],
  tabMode: TabMode = 'reuse'
): RouteTabMeta => ({
  title,
  tabMode,
  cacheStrategy: 'instance',
  getTabKey: ({ path, search }) =>
    buildPathWithPickedQuery(path, search, pickedKeys),
});

const isValidPerformanceStage = (stage: string | null): stage is string =>
  !!stage && Object.prototype.hasOwnProperty.call(PerformanceStageText, stage);

const getPerformanceStageTitle = (search: string, fallback: string) => {
  const searchParams = new URLSearchParams(search.replace(/^\?/, ''));
  const stage = searchParams.get('stage');
  return isValidPerformanceStage(stage)
    ? PerformanceStageText[stage]
    : fallback;
};

const performanceTodoTabMeta = (): RouteTabMeta => ({
  ...queryTabMeta('绩效首页-绩效待办', ['performance_id', 'stage']),
  getTitle: ({ search }) =>
    getPerformanceStageTitle(search, '绩效首页-绩效待办'),
});

const performanceHomeDetailTabMeta = (): RouteTabMeta => ({
  ...queryTabMeta('绩效首页-绩效详情', ['performance_id', 'account_id'], 'new'),
  getTitle: ({ search }) =>
    getPerformanceStageTitle(search, '绩效首页-绩效详情'),
});

const routeMetaTable: RouteMetaEntry[] = [
  {
    pattern: '/people/roster/collect/:id',
    meta: detailTabMeta('花名册采集详情'),
  },
  { pattern: '/people/regular/apply/:id', meta: detailTabMeta('转正审批详情') },
  {
    pattern: '/people/transfer/apply/:id',
    meta: detailTabMeta('调动审批详情'),
  },
  { pattern: '/people/resign/apply/:id', meta: detailTabMeta('离职审批详情') },
  {
    pattern: '/people/staff-apply/resign/:id',
    meta: detailTabMeta('人事申请离职详情'),
  },
  {
    pattern: '/people/staff-apply/collect/:id',
    meta: detailTabMeta('人事申请采集详情'),
  },
  {
    pattern: '/people/staff-apply/regular/:id',
    meta: detailTabMeta('人事申请转正详情'),
  },
  {
    pattern: '/people/staff-apply/transfer/:id',
    meta: detailTabMeta('人事申请调动详情'),
  },
  {
    pattern: '/people/entry/todo',
    meta: queryTabMeta('入职准备-待办', ['account_id'], 'new'),
  },
  { pattern: '/people/roster', meta: reuseTabMeta('花名册') },
  { pattern: '/people/entry', meta: reuseTabMeta('入职管理') },
  { pattern: '/people/regular', meta: reuseTabMeta('转正管理') },
  { pattern: '/people/transfer', meta: reuseTabMeta('调动管理') },
  { pattern: '/people/resign', meta: reuseTabMeta('离职管理') },
  { pattern: '/people/staff-apply', meta: reuseTabMeta('人事申请') },
  { pattern: '/people/trial', meta: reuseTabMeta('试岗期管理') },
  { pattern: '/people/blacklist', meta: reuseTabMeta('黑名单管理') },
  // 招聘管理：详情 pattern 须先于同级列表，避免 :id 误匹配
  { pattern: '/recruitment/hc/detail/:id', meta: detailTabMeta('HC审批详情') },
  {
    pattern: '/recruitment/position/detail/:id',
    meta: detailTabMeta('招聘职位详情'),
  },
  { pattern: '/recruitment/hc', meta: reuseTabMeta('HC管理') },
  { pattern: '/recruitment/position', meta: reuseTabMeta('招聘职位') },
  { pattern: '/recruitment/candidate', meta: reuseTabMeta('候选人管理') },
  { pattern: '/recruitment/interview', meta: reuseTabMeta('面试管理') },
  { pattern: '/recruitment/offer-audit', meta: reuseTabMeta('offer审批') },
  { pattern: '/recruitment/resume-screening', meta: reuseTabMeta('简历筛选') },
  { pattern: '/recruitment/talent-pool', meta: reuseTabMeta('人才库') },
  {
    pattern: '/recruitment/internal-recommendation',
    meta: reuseTabMeta('内推统计'),
  },
  { pattern: '/recruitment/hunter', meta: reuseTabMeta('猎头管理') },
  { pattern: '/offer-confirm', meta: reuseTabMeta('offer确认') },
  // 绩效管理：同级下更长的 path 须先匹配
  {
    pattern: '/performance/management/members',
    meta: queryTabMeta('绩效管理-被考核人', ['id']),
  },
  {
    pattern: '/performance/home/finished-todo',
    meta: queryTabMeta('绩效首页-已处理待办', ['performance_id', 'stage']),
  },
  {
    pattern: '/performance/home/todo',
    meta: performanceTodoTabMeta(),
  },
  {
    pattern: '/performance/home/detail',
    meta: performanceHomeDetailTabMeta(),
  },
  { pattern: '/performance/home', meta: reuseTabMeta('绩效首页') },
  {
    pattern: '/performance/my/detail',
    meta: queryTabMeta(
      '我的绩效-绩效详情',
      ['performance_id', 'account_id'],
      'new'
    ),
  },
  { pattern: '/performance/my', meta: reuseTabMeta('我的绩效') },
  {
    pattern: '/performance/appeal-approval/performance-detail',
    meta: queryTabMeta(
      '申诉审核-绩效详情',
      ['performance_id', 'account_id'],
      'new'
    ),
  },
  { pattern: '/performance/appeal-approval', meta: reuseTabMeta('申诉审核') },
  {
    pattern: '/performance/month/detail',
    meta: queryTabMeta(
      '月度绩效-绩效详情',
      ['performance_id', 'account_id'],
      'new'
    ),
  },
  { pattern: '/performance/month', meta: reuseTabMeta('月度绩效') },
  { pattern: '/performance/management', meta: reuseTabMeta('绩效管理') },
  { pattern: '/performance/my-appeal', meta: reuseTabMeta('我的申诉') },
  // 薪资管理
  {
    pattern: '/salary/increase/apply/:id',
    meta: detailTabMeta('调薪审批详情'),
  },
  { pattern: '/salary/archive', meta: reuseTabMeta('薪资档案') },
  { pattern: '/salary/plan', meta: reuseTabMeta('薪资方案管理') },
  { pattern: '/salary/increase', meta: reuseTabMeta('调薪管理') },
  { pattern: '/salary/compute', meta: reuseTabMeta('薪资核算') },
  // 组织管理
  { pattern: '/organization/part-time', meta: reuseTabMeta('兼岗管理') },
  { pattern: '/organization/department', meta: reuseTabMeta('部门管理') },
  { pattern: '/organization/job', meta: reuseTabMeta('职位管理') },
  { pattern: '/organization/rank', meta: reuseTabMeta('职级管理') },
  // 假勤管理
  { pattern: '/leave/manage/detail/:id', meta: detailTabMeta('请假审批详情') },
  {
    pattern: '/leave/out-official/detail/:id',
    meta: detailTabMeta('公出审批详情'),
  },
  {
    pattern: '/leave/out-business/detail/:id',
    meta: detailTabMeta('出差审批详情'),
  },
  { pattern: '/leave/balance', meta: reuseTabMeta('假期余额') },
  { pattern: '/leave/manage', meta: reuseTabMeta('请假管理') },
  { pattern: '/leave/out-official', meta: reuseTabMeta('公出') },
  { pattern: '/leave/out-business', meta: reuseTabMeta('出差') },
  // 考勤管理（含动态段非 id）
  {
    pattern: '/attendance/schedule/:groupId',
    meta: detailTabMetaWithParam('编辑排班', 'groupId'),
  },
  {
    pattern: '/attendance/overtime/detail/:id',
    meta: detailTabMeta('加班审批详情'),
  },
  {
    pattern: '/attendance/shift-transfer/detail/:id',
    meta: detailTabMeta('调班审批详情'),
  },
  { pattern: '/attendance/shift-transfer', meta: reuseTabMeta('调班管理') },
  { pattern: '/attendance/team-attendance', meta: reuseTabMeta('团队考勤') },
  { pattern: '/attendance/overtime', meta: reuseTabMeta('加班管理') },
  { pattern: '/attendance/my-attendance', meta: reuseTabMeta('我的考勤') },
  { pattern: '/attendance/stat', meta: reuseTabMeta('出勤统计') },
  { pattern: '/attendance/schedule', meta: reuseTabMeta('排班管理') },
  { pattern: '/attendance/group', meta: reuseTabMeta('考勤组管理') },
  { pattern: '/attendance/shift', meta: reuseTabMeta('班次管理') },
  // 系统管理
  {
    pattern: '/system/approval-template/detail/:code',
    meta: detailTabMetaWithParam('审批模板详情', 'code'),
  },
  {
    pattern: '/system/data/:dataCode',
    meta: detailTabMetaWithParam('数组配置详情', 'dataCode'),
  },
  {
    pattern: '/system/prole/people',
    meta: {
      title: '人员角色',
      tabMode: 'reuse',
      cacheStrategy: 'instance',
      getTabKey: () => '/system/prole',
      getKeepAliveCacheKey: ({ path, search }) =>
        buildTabRouteCacheKey(path, search),
    },
  },
  {
    pattern: '/system/prole/role',
    meta: {
      title: '人员角色',
      tabMode: 'reuse',
      cacheStrategy: 'instance',
      getTabKey: () => '/system/prole',
      getKeepAliveCacheKey: ({ path, search }) =>
        buildTabRouteCacheKey(path, search),
    },
  },
  { pattern: '/system/prole', meta: reuseTabMeta('人员角色') },
  { pattern: '/system/leave-plan', meta: reuseTabMeta('假勤方案') },
  { pattern: '/system/setting', meta: reuseTabMeta('系统配置') },
  {
    pattern: '/system/department-approval',
    meta: reuseTabMeta('部门审批流程'),
  },
  { pattern: '/system/data', meta: reuseTabMeta('数组配置') },
  { pattern: '/system/log', meta: reuseTabMeta('日志') },
  {
    pattern: '/system/multi-organization',
    meta: reuseTabMeta('多组织管理'),
  },
  { pattern: '/system/approval-template', meta: reuseTabMeta('审批模板管理') },
  { pattern: '/system/assistant', meta: reuseTabMeta('智能助手管理') },
];

const fallbackMeta: RouteTabMeta = {
  title: '',
  tabMode: 'reuse',
  cacheStrategy: 'none',
  getTabKey: ({ path, search }) => buildTabRouteCacheKey(path, search),
};

/**
 * 桌面端业务路由挂在 `/org/:orgId` 下，与 routeMeta 中不带 org 前缀的 pattern 对齐。
 */
export function stripOrgSegmentFromPathname(pathname: string): string {
  if (!pathname.startsWith('/org/')) {
    return pathname;
  }
  const rest = pathname.replace(/^\/org\/[^/]+/, '');
  if (!rest) {
    return '/';
  }
  return rest.startsWith('/') ? rest : `/${rest}`;
}

/** 组织根路径（如 `/org/:orgId`），不应作为业务标签打开 */
export function isOrgRootPathname(pathname: string): boolean {
  return stripOrgSegmentFromPathname(pathname) === '/';
}

/** 去掉 `/org/:orgId` 前缀得到菜单/路由 key；兼容无尾斜杠的组织根路径 */
export function transRoutePathToKey(path: string) {
  const stripped = path.replace(/^\/org\/[^/]+(?=\/|$)/, '');
  if (!stripped || stripped === '/') {
    return '';
  }
  return stripped.replace(/^\//, '');
}

export const resolveRouteTabMeta = (
  path: string,
  search: string,
  _fullPath: string
) => {
  const pathForMeta = stripOrgSegmentFromPathname(path);
  const fullPathForMeta =
    pathForMeta === path ? _fullPath : `${pathForMeta}${search}`;

  for (const entry of routeMetaTable) {
    const params = matchPattern(entry.pattern, pathForMeta);
    if (!params) continue;
    const ctx: RouteMatchContext = {
      path: pathForMeta,
      search,
      fullPath: fullPathForMeta,
      params,
    };
    const tabKey = entry.meta.getTabKey(ctx);
    return {
      ...entry.meta,
      title: entry.meta.getTitle?.(ctx) ?? entry.meta.title,
      tabKey,
      keepAliveCacheKey: entry.meta.getKeepAliveCacheKey?.(ctx) ?? tabKey,
      params,
    };
  }
  const fallbackCtx: RouteMatchContext = {
    path: pathForMeta,
    search,
    fullPath: fullPathForMeta,
    params: {},
  };
  const fallbackTabKey = fallbackMeta.getTabKey(fallbackCtx);
  return {
    ...fallbackMeta,
    tabKey: fallbackTabKey,
    keepAliveCacheKey: fallbackTabKey,
    params: {},
  };
};
